import { useMemo, useState } from "react";
import { apiFetch } from "../../lib/api";
import type { Source } from "./KnowledgeWorkbench";

interface SourcesPanelProps {
  kbId: string;
  sources: Source[];
  onRefresh: () => void;
}

const SOURCE_TYPES: Source["source_type"][] = ["file", "url", "text", "git"];

export function SourcesPanel({ kbId, sources, onRefresh }: SourcesPanelProps) {
  const [sourceType, setSourceType] = useState<Source["source_type"]>("file");
  const [path, setPath] = useState("");
  const [label, setLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [indexingId, setIndexingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const indexedCount = useMemo(
    () => sources.filter((source) => source.status === "indexed").length,
    [sources],
  );

  async function addSource() {
    if (!path.trim()) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await apiFetch(`/api/knowledge/${kbId}/sources`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source_type: sourceType,
          path,
          label,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.error) {
        throw new Error(data.error || "Failed to add source");
      }
      setPath("");
      setLabel("");
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add source");
    } finally {
      setSubmitting(false);
    }
  }

  async function indexSources(sourceId?: string) {
    setIndexingId(sourceId || "__all__");
    setError(null);
    try {
      const response = await apiFetch(`/api/knowledge/${kbId}/index`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sourceId ? { source_id: sourceId } : {}),
      });
      const data = await response.json();
      if (!response.ok || data.error) {
        throw new Error(data.error || "Failed to index source");
      }
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to index source");
    } finally {
      setIndexingId(null);
    }
  }

  async function removeSource(sourceId: string) {
    setError(null);
    try {
      const response = await apiFetch(`/api/knowledge/${kbId}/sources/${sourceId}`, {
        method: "DELETE",
      });
      const data = await response.json();
      if (!response.ok || data.error) {
        throw new Error(data.error || "Failed to remove source");
      }
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to remove source");
    }
  }

  return (
    <div style={S.root}>
      <div style={S.header}>
        <div>
          <h3 style={S.heading}>Source Registry</h3>
          <p style={S.subheading}>
            {sources.length} sources tracked, {indexedCount} indexed
          </p>
        </div>
        <button
          style={{ ...S.actionBtn, opacity: sources.length === 0 ? 0.45 : 1 }}
          disabled={sources.length === 0 || indexingId !== null}
          onClick={() => indexSources()}
        >
          {indexingId === "__all__" ? "Indexing..." : "Index All Pending"}
        </button>
      </div>

      <div style={S.card}>
        <div style={S.formGrid}>
          <select
            style={S.select}
            value={sourceType}
            onChange={(event) => setSourceType(event.target.value as Source["source_type"])}
          >
            {SOURCE_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <input
            style={S.input}
            placeholder={
              sourceType === "file"
                ? "/abs/path/to/file"
                : sourceType === "url"
                  ? "https://example.com"
                  : sourceType === "git"
                    ? "repo/path/or/branch"
                    : "Paste text or summary pointer"
            }
            value={path}
            onChange={(event) => setPath(event.target.value)}
          />
          <input
            style={S.input}
            placeholder="Label (optional)"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
          />
          <button
            style={{ ...S.primaryBtn, opacity: !path.trim() || submitting ? 0.45 : 1 }}
            disabled={!path.trim() || submitting}
            onClick={addSource}
          >
            {submitting ? "Adding..." : "Add Source"}
          </button>
        </div>
        {error && <div style={S.error}>{error}</div>}
      </div>

      <div style={S.list}>
        {sources.length === 0 ? (
          <div style={S.empty}>
            Add files, docs, URLs, or notes here. Indexing currently creates basic file-backed nodes and
            leaves the rest available for future ingestion flows.
          </div>
        ) : (
          sources.map((source) => (
            <div key={source.id} style={S.row}>
              <div style={S.rowMain}>
                <div style={S.rowTop}>
                  <span style={S.label}>{source.label}</span>
                  <span style={{ ...S.badge, ...badgeStyle(source.status) }}>{source.status}</span>
                </div>
                <div style={S.path}>{source.path}</div>
                <div style={S.meta}>
                  <span>{source.source_type}</span>
                  <span>{source.chunk_count} chunks</span>
                  <span>{source.last_indexed ? formatDate(source.last_indexed) : "not indexed yet"}</span>
                </div>
              </div>
              <div style={S.rowActions}>
                <button
                  style={{ ...S.secondaryBtn, opacity: indexingId && indexingId !== source.id ? 0.5 : 1 }}
                  disabled={indexingId !== null}
                  onClick={() => indexSources(source.id)}
                >
                  {indexingId === source.id ? "Indexing..." : "Index"}
                </button>
                <button style={S.deleteBtn} onClick={() => removeSource(source.id)}>
                  Remove
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function formatDate(value: string) {
  try {
    return new Date(value).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
}

function badgeStyle(status: Source["status"]) {
  switch (status) {
    case "indexed":
      return { color: "#4f8fff", borderColor: "rgba(79,143,255,0.25)", background: "rgba(79,143,255,0.08)" };
    case "error":
      return { color: "#ff7b72", borderColor: "rgba(255,123,114,0.25)", background: "rgba(255,123,114,0.08)" };
    case "stale":
      return { color: "#f2cc60", borderColor: "rgba(242,204,96,0.25)", background: "rgba(242,204,96,0.08)" };
    default:
      return { color: "#8b949e", borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.04)" };
  }
}

const S: Record<string, React.CSSProperties> = {
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    padding: "1rem",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
  },
  heading: {
    margin: 0,
    fontSize: "0.95rem",
    color: "var(--ot-text-primary, #e6edf3)",
  },
  subheading: {
    margin: "0.2rem 0 0",
    fontSize: "0.72rem",
    color: "var(--ot-text-secondary, #8b949e)",
  },
  card: {
    padding: "0.9rem",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.06)",
    background: "rgba(255,255,255,0.02)",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "120px 1.5fr 1fr 140px",
    gap: "0.6rem",
  },
  select: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "6px",
    color: "var(--ot-text-primary, #e6edf3)",
    padding: "0.55rem 0.65rem",
    fontSize: "0.78rem",
    fontFamily: "var(--ot-font-mono, 'JetBrains Mono', monospace)",
  },
  input: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "6px",
    color: "var(--ot-text-primary, #e6edf3)",
    padding: "0.55rem 0.65rem",
    fontSize: "0.78rem",
    fontFamily: "var(--ot-font-mono, 'JetBrains Mono', monospace)",
  },
  primaryBtn: {
    border: "none",
    borderRadius: "6px",
    background: "var(--ot-accent, #4f8fff)",
    color: "var(--ot-bg-deep, #050608)",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: "0.75rem",
    fontFamily: "var(--ot-font-mono, 'JetBrains Mono', monospace)",
  },
  actionBtn: {
    padding: "0.55rem 0.8rem",
    borderRadius: "6px",
    border: "1px solid rgba(79,143,255,0.25)",
    background: "rgba(79,143,255,0.08)",
    color: "var(--ot-accent, #4f8fff)",
    cursor: "pointer",
    fontSize: "0.72rem",
    fontFamily: "var(--ot-font-mono, 'JetBrains Mono', monospace)",
  },
  error: {
    marginTop: "0.6rem",
    color: "#ff7b72",
    fontSize: "0.72rem",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
  },
  empty: {
    padding: "1.4rem",
    textAlign: "center",
    color: "var(--ot-text-muted, #6e7681)",
    border: "1px dashed rgba(255,255,255,0.08)",
    borderRadius: "10px",
    lineHeight: 1.6,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: "0.75rem",
    alignItems: "center",
    padding: "0.85rem 0.95rem",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.06)",
    background: "rgba(255,255,255,0.02)",
  },
  rowMain: {
    minWidth: 0,
  },
  rowTop: {
    display: "flex",
    alignItems: "center",
    gap: "0.6rem",
    marginBottom: "0.25rem",
  },
  label: {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: "var(--ot-text-primary, #e6edf3)",
  },
  badge: {
    fontSize: "0.63rem",
    padding: "0.15rem 0.45rem",
    borderRadius: "999px",
    border: "1px solid rgba(255,255,255,0.08)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  path: {
    fontSize: "0.72rem",
    color: "var(--ot-text-secondary, #8b949e)",
    wordBreak: "break-all",
  },
  meta: {
    display: "flex",
    gap: "0.7rem",
    marginTop: "0.4rem",
    fontSize: "0.66rem",
    color: "var(--ot-text-muted, #6e7681)",
    flexWrap: "wrap",
  },
  rowActions: {
    display: "flex",
    gap: "0.45rem",
  },
  secondaryBtn: {
    padding: "0.45rem 0.7rem",
    borderRadius: "6px",
    border: "1px solid rgba(255,255,255,0.08)",
    background: "rgba(255,255,255,0.04)",
    color: "var(--ot-text-primary, #e6edf3)",
    cursor: "pointer",
    fontSize: "0.72rem",
    fontFamily: "var(--ot-font-mono, 'JetBrains Mono', monospace)",
  },
  deleteBtn: {
    padding: "0.45rem 0.7rem",
    borderRadius: "6px",
    border: "1px solid rgba(255,123,114,0.2)",
    background: "rgba(255,123,114,0.08)",
    color: "#ff7b72",
    cursor: "pointer",
    fontSize: "0.72rem",
    fontFamily: "var(--ot-font-mono, 'JetBrains Mono', monospace)",
  },
};
